import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, UserPlus, RefreshCcw, X, Trash2, Copy,
  ShieldCheck, User, Clock, MessageSquare, Upload as UploadIcon,
  LogIn, UserCog, Loader2,
} from 'lucide-react';
import { API, useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Logo } from '@/components/Logo';
import { AdminNav } from '@/components/AdminNav';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ADMIN_USERS } from '@/constants/testIds';
import { toast } from 'sonner';

const ACTIVITY_META = {
  login: { icon: LogIn, label: 'Signed in' },
  chat: { icon: MessageSquare, label: 'Asked a question' },
  upload_document: { icon: UploadIcon, label: 'Uploaded a document' },
  invite_user: { icon: UserPlus, label: 'Invited a user' },
  resend_invite: { icon: RefreshCcw, label: 'Resent an invitation' },
  revoke_invite: { icon: X, label: 'Revoked an invitation' },
  delete_invite: { icon: Trash2, label: 'Deleted an invitation' },
  enable_user: { icon: ShieldCheck, label: 'Enabled a user' },
  disable_user: { icon: X, label: 'Disabled a user' },
  update_user_role: { icon: UserCog, label: 'Changed a user role' },
  delete_user: { icon: Trash2, label: 'Deleted a user' },
  accept_invite: { icon: UserPlus, label: 'Joined via invite' },
};

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function UsersAdminPage() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i, a] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/invites`, { withCredentials: true }),
        axios.get(`${API}/admin/activity?limit=200`, { withCredentials: true }),
      ]);
      setUsers(u.data);
      setInvites(i.data);
      setActivity(a.data);
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/users/${u.id}`,
        { is_active: !u.is_active },
        { withCredentials: true }
      );
      toast.success(`${data.email} ${data.is_active ? 'enabled' : 'disabled'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update user');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { withCredentials: true });
      toast.success(`${u.email} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resendInvite = async (inv) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/invites/${inv.id}/resend`,
        {},
        { withCredentials: true }
      );
      if (data.accept_url_debug) {
        toast.success('Invite refreshed. Link copied to clipboard.');
        try { await navigator.clipboard.writeText(data.accept_url_debug); } catch {}
      } else {
        toast.success('Invite email resent');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resend');
    }
  };

  const toggleRole = async (u) => {
    const nextRole = u.role === 'admin' ? 'user' : 'admin';
    const verb = nextRole === 'admin' ? 'Promote to admin' : 'Demote to user';
    if (!window.confirm(`${verb}: ${u.email}?`)) return;
    try {
      const { data } = await axios.patch(
        `${API}/admin/users/${u.id}`,
        { role: nextRole },
        { withCredentials: true }
      );
      toast.success(`${data.email} is now ${data.role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update role');
    }
  };

  const deleteInvite = async (inv) => {
    if (!window.confirm(`Delete invitation for ${inv.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/invites/${inv.id}`, { withCredentials: true });
      toast.success('Invitation deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div data-testid={ADMIN_USERS.page} className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3">
            <Logo variant="mark" className="h-8 w-8 rounded-md" alt="STPK" />
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#0A2540] gap-2 px-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to categories</span>
            </Button>
          </Link>
          <div className="h-6 w-px bg-slate-200" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
              Administration
            </div>
            <h1 className="font-display text-lg text-[#0A2540] leading-tight">
              User Management
            </h1>
          </div>
          <div className="ml-auto">
            <AdminNav />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-3xl text-[#0A2540]">Team &amp; access</h2>
            <p className="mt-2 text-sm text-slate-500 max-w-2xl">
              Invite teammates, enable or disable accounts, and monitor platform activity.
            </p>
          </div>
          <InviteDialog
            open={inviteOpen}
            setOpen={setInviteOpen}
            onCreated={load}
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 h-auto bg-white border border-slate-200 p-1 rounded-md w-full max-w-lg">
            <TabsTrigger data-testid={ADMIN_USERS.tabUsers} value="users"
              className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-sm py-2.5 text-sm">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger data-testid={ADMIN_USERS.tabInvites} value="invites"
              className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-sm py-2.5 text-sm">
              Invites ({invites.filter(i => !i.is_used && !i.is_revoked).length})
            </TabsTrigger>
            <TabsTrigger data-testid={ADMIN_USERS.tabActivity} value="activity"
              className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-sm py-2.5 text-sm">
              Activity ({activity.length})
            </TabsTrigger>
          </TabsList>

          {/* Users tab */}
          <TabsContent value="users" className="mt-8">
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">User</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Role</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Status</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Chats</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Last active</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                    </TableCell></TableRow>
                  )}
                  {!loading && users.map((u) => {
                    const isMe = u.id === me?.id;
                    return (
                      <TableRow key={u.id} data-testid={ADMIN_USERS.userRow} className="border-slate-100 hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-md border flex items-center justify-center ${u.role === 'admin' ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-slate-200'}`}>
                              {u.role === 'admin'
                                ? <ShieldCheck className="h-4 w-4 text-[#D4AF37]" />
                                : <User className="h-4 w-4 text-slate-500" />}
                            </div>
                            <div>
                              <div className="font-medium text-[#0A2540] text-sm">
                                {u.name || u.email.split('@')[0]}
                                {isMe && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">(you)</span>}
                              </div>
                              <div className="text-xs text-slate-500">{u.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => toggleRole(u)}
                            disabled={isMe}
                            title={isMe ? 'You cannot change your own role' : `Click to ${u.role === 'admin' ? 'demote to user' : 'promote to admin'}`}
                            data-testid={`admin-toggle-role-btn-${u.id}`}
                            className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors ${
                              u.role === 'admin'
                                ? 'bg-[#0A2540] text-white hover:bg-slate-800'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            {u.role === 'admin' ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                            {u.role}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={u.is_active !== false}
                              onCheckedChange={() => toggleActive(u)}
                              disabled={isMe}
                              data-testid={ADMIN_USERS.toggleActiveBtn}
                            />
                            <span className={`text-xs ${u.is_active !== false ? 'text-green-700' : 'text-slate-400'}`}>
                              {u.is_active !== false ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm font-mono text-slate-500">{u.chat_count || 0}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3 text-slate-300" />
                            {fmt(u.last_active_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => deleteUser(u)}
                            disabled={isMe}
                            data-testid={ADMIN_USERS.deleteUserBtn}
                            className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && users.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-16 text-center text-slate-400 text-sm">
                      No users yet.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </section>
          </TabsContent>

          {/* Invites tab */}
          <TabsContent value="invites" className="mt-8">
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Invitee</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Role</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Status</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Sent</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Expires</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((inv) => {
                    const status = inv.is_used
                      ? { label: 'Accepted', color: 'text-green-700 bg-green-50 border-green-200' }
                      : inv.is_revoked
                      ? { label: 'Revoked', color: 'text-slate-400 bg-slate-50 border-slate-200' }
                      : new Date(inv.expires_at) < new Date()
                      ? { label: 'Expired', color: 'text-slate-400 bg-slate-50 border-slate-200' }
                      : { label: 'Pending', color: 'text-[#0A2540] bg-[#D4AF37]/10 border-[#D4AF37]/40' };
                    const canAct = !inv.is_used;
                    return (
                      <TableRow key={inv.id} data-testid={ADMIN_USERS.inviteRow} className="border-slate-100 hover:bg-slate-50">
                        <TableCell>
                          <div className="font-medium text-[#0A2540] text-sm">{inv.name || inv.email.split('@')[0]}</div>
                          <div className="text-xs text-slate-500">{inv.email}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 capitalize">{inv.role}</TableCell>
                        <TableCell>
                          <span className={`inline-flex text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${status.color}`}>
                            {status.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{fmt(inv.created_at)}</TableCell>
                        <TableCell className="text-sm text-slate-500">{fmt(inv.expires_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {!inv.is_used && (
                              <Button variant="ghost" size="sm"
                                onClick={() => resendInvite(inv)}
                                data-testid={ADMIN_USERS.resendInviteBtn}
                                className="text-slate-500 hover:text-[#0A2540]"
                                title="Resend invitation">
                                <RefreshCcw className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm"
                              onClick={() => deleteInvite(inv)}
                              data-testid={ADMIN_USERS.deleteInviteBtn}
                              className="text-slate-400 hover:text-red-600"
                              title="Delete invitation">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {invites.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-16 text-center text-slate-400 text-sm">
                      No invitations yet. Click <span className="text-[#0A2540] font-medium">Invite user</span> to send one.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </section>
          </TabsContent>

          {/* Activity tab */}
          <TabsContent value="activity" className="mt-8">
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="divide-y divide-slate-100">
                {activity.map((a) => {
                  const meta = ACTIVITY_META[a.action] || { icon: Clock, label: a.action };
                  const Icon = meta.icon;
                  const detail = a.category
                    ? ` in ${a.category.replace('-', ' ')}`
                    : a.target_email
                    ? ` — ${a.target_email}`
                    : a.document_title
                    ? ` — ${a.document_title}`
                    : '';
                  return (
                    <div
                      key={a.id}
                      data-testid={ADMIN_USERS.activityRow}
                      className="flex items-start gap-4 px-6 py-4"
                    >
                      <div className="h-9 w-9 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-4 w-4 text-[#0A2540]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#0A2540]">
                          <span className="font-medium">{a.user_email}</span>
                          <span className="text-slate-500"> · {meta.label}{detail}</span>
                        </div>
                        {a.question && (
                          <div className="mt-1 text-xs text-slate-500 truncate italic">
                            "{a.question}"
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 whitespace-nowrap">{fmt(a.created_at)}</div>
                    </div>
                  );
                })}
                {activity.length === 0 && (
                  <div className="py-16 text-center text-slate-400 text-sm">No activity recorded yet.</div>
                )}
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}


function InviteDialog({ open, setOpen, onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('user');
  const [submitting, setSubmitting] = useState(false);
  const [debugUrl, setDebugUrl] = useState('');

  const reset = () => {
    setEmail(''); setName(''); setRole('user'); setDebugUrl('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/invites`,
        { email, name: name || undefined, role },
        { withCredentials: true }
      );
      if (data.accept_url_debug) {
        setDebugUrl(data.accept_url_debug);
        toast.success('Invite created. Copy the link below (Resend not configured).');
      } else {
        toast.success(`Invitation sent to ${email}`);
        setOpen(false);
        reset();
      }
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          data-testid={ADMIN_USERS.inviteBtn}
          className="bg-[#0A2540] hover:bg-slate-800 text-white h-11 rounded-md px-5 gap-2"
        >
          <UserPlus className="h-4 w-4" /> Invite user
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={ADMIN_USERS.inviteDialog} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A2540]">Invite a teammate</DialogTitle>
          <DialogDescription className="text-slate-500">
            They'll receive an email with a secure link to set their password.
          </DialogDescription>
        </DialogHeader>

        {debugUrl ? (
          <div className="space-y-4">
            <div className="text-xs uppercase tracking-wider text-[#D4AF37]">
              Resend is not configured
            </div>
            <p className="text-sm text-slate-500">
              Copy this invitation link and share it with{' '}
              <span className="text-[#0A2540] font-medium">{email}</span>:
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs font-mono break-all text-[#0A2540]">
              {debugUrl}
            </div>
            <Button
              onClick={async () => {
                try { await navigator.clipboard.writeText(debugUrl); toast.success('Link copied'); } catch {}
              }}
              className="w-full h-10 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md gap-2"
            >
              <Copy className="h-4 w-4" /> Copy link
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setOpen(false); reset(); }}
              className="w-full"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                type="email" required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={ADMIN_USERS.inviteEmailInput}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="colleague@spwm.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Name (optional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={ADMIN_USERS.inviteNameInput}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="Jane Portfolio"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid={ADMIN_USERS.inviteRoleSelect} className="h-11 rounded-md border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={submitting}
                data-testid={ADMIN_USERS.inviteSubmit}
                className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md gap-2"
              >
                <UserPlus className="h-4 w-4" />
                {submitting ? 'Sending…' : 'Send invitation'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
